/**
 * Hook to assign moderation cases for a topic
 */

import { useLazyQuery, useApolloClient } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import type { AssignModerationCaseQuery, AssignModerationCaseQueryVariables } from '@core/graphql/types/operations';
import { useMemo, useRef, useCallback } from 'react';

import { ASSIGN_MODERATION_CASE } from '../graphql';

/**
 * Hook to assign moderation cases to the current moderator
 * @returns Lazy query function to assign cases
 */
export function useAssignModerationCase() {
  const client = useApolloClient();
  const lastVariablesRef = useRef<AssignModerationCaseQueryVariables | null>(null);

  const [executeLazyQuery, { data, loading, error }] = useLazyQuery<AssignModerationCaseQuery>(ASSIGN_MODERATION_CASE, {
    fetchPolicy: 'network-only', // Always fetch fresh cases from server
    onCompleted: responseData => {
      // After cases are assigned, evict stats to reflect updated counts
      // Use tracked query variables (not response data) for reliable eviction
      if (responseData?.assignModerationCase?.length && lastVariablesRef.current) {
        client.cache.evict({
          fieldName: 'topicModerationStats',
          args: {
            communityId: lastVariablesRef.current.communityId,
            topicName: lastVariablesRef.current.topicName,
          },
        });
        client.cache.gc();
      }
    },
  });

  // Wrapper that tracks variables before executing the query
  const assignCases = useCallback(
    (options: { variables: AssignModerationCaseQueryVariables }) => {
      lastVariablesRef.current = options.variables;

      return executeLazyQuery(options);
    },
    [executeLazyQuery]
  );

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const cases = data?.assignModerationCase?.filter(Boolean) || [];

  return {
    assignCases,
    cases,
    loading,
    errors,
  };
}
